import { MinusIcon } from "@phosphor-icons/react/dist/csr/Minus";
import { lazy, Suspense, useEffect } from "react";
import { Badge } from "@volca/ui/components/ui/badge";
import { Button } from "@volca/ui/components/ui/button";
import { Stack } from "@volca/ui/components/ui/stack";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@volca/ui/components/ui/tooltip";
import { useIsMobile } from "@volca/ui/hooks/use-mobile";
import { cn } from "@volca/ui/lib/utils";
import { useEnv } from "@/core/env";
import { useImpersonation } from "@/core/impersonation";
import { USER_ROLES } from "@/user/constants";
import { useGetMyUser } from "@/user/query";
import { DevBarImpersonation } from "./dev-bar-impersonation";
import {
  DEV_BAR_HEIGHT,
  FRAME_WIDTH,
  MOBILE_DEV_BAR_HEIGHT,
  MOBILE_IMPERSONATION_BAR_HEIGHT,
} from "./dev-bar-layout";
import { devBarPreference, useDevBarVisible } from "./dev-bar-preference";
import { DevSessionPopover } from "./dev-session-popover";

const Tools = import.meta.env.DEV
  ? lazy(() =>
      import("./dev-bar-tools").then((module) => ({
        default: module.DevBarTools,
      })),
    )
  : null;
const ENV_LABEL = {
  development: "DEV",
  staging: "Staging",
  demo: "Demo",
  production: "Production",
} as const;
const ENV_COLOR = {
  development: "information",
  staging: "warning",
  demo: "warning",
  production: "danger",
} as const;

export const useDevBarEligible = () => {
  const { appEnv } = useEnv();
  const { data: user } = useGetMyUser();
  const { isImpersonating } = useImpersonation();
  return (
    appEnv !== "production" ||
    user?.role === USER_ROLES.ADMIN ||
    isImpersonating
  );
};

export const useShowDevBar = () => {
  const eligible = useDevBarEligible();
  const visible = useDevBarVisible();
  const { isImpersonating } = useImpersonation();
  const isMobile = useIsMobile();
  const show = eligible && (visible || isImpersonating);
  const barHeight = isMobile
    ? isImpersonating
      ? MOBILE_IMPERSONATION_BAR_HEIGHT
      : MOBILE_DEV_BAR_HEIGHT
    : DEV_BAR_HEIGHT;
  const height = isImpersonating
    ? `calc(${barHeight} + ${FRAME_WIDTH})`
    : barHeight;
  useEffect(() => {
    document.documentElement.style.setProperty(
      "--app-devbar-height",
      show ? height : "0px",
    );
    return () => {
      document.documentElement.style.removeProperty("--app-devbar-height");
    };
  }, [height, show]);
  return show;
};

export const DevBar = ({ onImpersonate }: { onImpersonate?: () => void }) => {
  const { appEnv, apiUrl } = useEnv();
  const { isImpersonating } = useImpersonation();
  return (
    <Stack
      direction="row"
      align="center"
      gap={0}
      role="region"
      aria-label="Developer bar"
      className={cn(
        "dark h-8 min-w-0 shrink-0 bg-surface-sunken text-ink max-md:h-11",
        {
          "max-md:h-25 max-md:flex-wrap": isImpersonating,
        },
      )}
    >
      <Stack
        direction="row"
        align="center"
        className="shrink-0 px-3 max-md:hidden"
        title={`${appEnv} · ${apiUrl}`}
      >
        <Badge size="sm" color={ENV_COLOR[appEnv]} variant="ghost">
          {ENV_LABEL[appEnv]}
        </Badge>
      </Stack>
      <DevBarImpersonation onImpersonate={onImpersonate} />
      {Tools && (
        <Suspense fallback={null}>
          <Tools />
        </Suspense>
      )}
      <Stack
        direction="row"
        align="center"
        gap={1}
        className="ml-auto shrink-0 px-2 max-md:h-11"
      >
        <DevSessionPopover />
        {!isImpersonating && (
          <Tooltip>
            <TooltipTrigger
              render={
                <Button
                  size="icon-xs"
                  variant="ghost"
                  className="size-8 max-md:size-11"
                  aria-label="Hide developer bar"
                  onClick={() => devBarPreference.set(false)}
                />
              }
            >
              <MinusIcon aria-hidden />
            </TooltipTrigger>
            <TooltipContent sideOffset={8}>Hide developer bar</TooltipContent>
          </Tooltip>
        )}
      </Stack>
    </Stack>
  );
};
